import { Check, Copy, Download } from 'lucide-react';
import { useState } from 'react';

import {
  downloadText,
  tickerList,
  tradingViewList,
  type ExportSymbol,
} from '../../lib/symbolExport';

const buttonClass =
  'inline-flex h-8 items-center gap-1 rounded border border-border bg-bg px-2 text-xs text-text-muted hover:bg-surface-raised hover:text-text disabled:cursor-not-allowed disabled:opacity-40';

export function SymbolExport({
  label,
  fileName,
  rows,
}: {
  /** Names the list in button labels, e.g. "Tight coil 1M". */
  label: string;
  fileName: string;
  rows: readonly ExportSymbol[];
}) {
  const [status, setStatus] = useState<'idle' | 'copied' | 'failed'>('idle');
  const empty = rows.length === 0;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(tickerList(rows));
      setStatus('copied');
    } catch {
      setStatus('failed');
    }
  };

  return (
    <div className="flex items-center gap-1">
      <button
        type="button"
        aria-label={`Copy ${label} tickers`}
        title="Copy tickers"
        disabled={empty}
        onClick={() => void copy()}
        className={buttonClass}
      >
        {status === 'copied' ? (
          <Check className="h-3.5 w-3.5" aria-hidden="true" />
        ) : (
          <Copy className="h-3.5 w-3.5" aria-hidden="true" />
        )}
        Copy
      </button>
      <button
        type="button"
        aria-label={`Download ${label} as a TradingView list`}
        title="Download a TradingView watchlist file"
        disabled={empty}
        onClick={() => downloadText(fileName, tradingViewList(rows))}
        className={buttonClass}
      >
        <Download className="h-3.5 w-3.5" aria-hidden="true" />
        .txt
      </button>
      <span role="status" className="sr-only">
        {status === 'copied'
          ? `${label} tickers copied`
          : status === 'failed'
            ? 'Copy failed: the browser blocked clipboard access'
            : ''}
      </span>
      {status === 'failed' ? (
        <span className="text-[11px] text-danger">Clipboard blocked</span>
      ) : null}
    </div>
  );
}
